/**
 * Whether the signed-in account may use the News Tool — one call to /api/me.
 *
 * Signing in proves who someone is; the account's Entra groups decide whether
 * they get in (`src/app/core/rbac.py`). A 403 means none of the groups admits
 * them, and the shell says so once instead of letting every page fail on its
 * own. Any other answer, a network error included, opens the shell: the pages
 * report their own errors, and the backend refuses every call anyway.
 */

import { useEffect, useState } from 'react'

import { useAuth } from './useAuth'

export type Access = 'checking' | 'granted' | 'denied'

export function useAccess(): Access {
    const { isAuthenticated, getToken } = useAuth()
    const [access, setAccess] = useState<Access>('checking')

    useEffect(() => {
        if (!isAuthenticated) return
        let cancelled = false
        void (async () => {
            let verdict: Access = 'granted'
            try {
                const token = await getToken()
                const response = await fetch('/api/me', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                })
                if (response.status === 403) verdict = 'denied'
            } catch {
                // Leave it to the pages: they show the error where it happens.
            }
            if (!cancelled) setAccess(verdict)
        })()
        return () => {
            cancelled = true
        }
    }, [isAuthenticated, getToken])

    return access
}
